import React, { useEffect, useRef, useState } from 'react';
import TopicSelector from '../../components/TopicSelector';
import { useInfoCardStacks } from '../../hooks/queries/useInfoCards';
import { useLanguage } from '../../lib/i18n';
import { useConfig } from '../../hooks';
import { Card } from '../../components/ui/card';
import SelectedCards from './SelectedCardsSection';

interface Props {
  highlightedTopic: string | null;
  topic: string | null;
  handleTopicSelect: (topic: string) => void;
}

const TopicsSection = ({ highlightedTopic, topic, handleTopicSelect }: Props) => {
  const selectorRef = useRef<HTMLDivElement>(null);
  const childrenRef = useRef<HTMLDivElement>(null);

  const [showCards, setShowCards] = useState(false);

  const { language } = useLanguage();
  const { data: config } = useConfig();
  const { data } = useInfoCardStacks(language, config);
  const topicStacks = topic && data ? data.getStacksForCategory(topic) : [];
  const stackIds = data ? data.allStacks.map((stack) => stack.id) : [];

  // When show cards is closed, set the min-height of the children to 0
  useEffect(() => {
    if (selectorRef.current) {
      if (!showCards && childrenRef.current) {
        childrenRef.current.style.minHeight = '0px';
        childrenRef.current.style.opacity = '0';
        childrenRef.current.style.gridTemplateRows = '0fr';
      }
      if (showCards && childrenRef.current) {
        childrenRef.current.style.minHeight = selectorRef.current?.offsetHeight + 'px';
        childrenRef.current.style.opacity = '1';
        childrenRef.current.style.gridTemplateRows = '1fr';
      }
    }
  }, [showCards, childrenRef, selectorRef]);

  const onTopicSelect = (topic: string) => {
    setShowCards(true);
    handleTopicSelect(topic);
  };

  const onClose = async () => {
    setShowCards(false);
  };

  if (!data || data.categories.length === 0 || data.allStacks.length === 0) {
    return null;
  }

  return (
    <Card className="animate-slide-up-3 opacity-0">
      <div className="flex flex-row justify-between overflow-hidden">
        <div
          style={{
            flex: '1 0 100%',
            transition: 'opacity 0.25s, min-height 0.4s ease-out',
          }}
          ref={selectorRef}
          className={`grid transform ${
            showCards ? 'animate-slide-topic-left' : 'animate-slide-topic-right'
          }`}
        >
          <TopicSelector
            onTopicSelect={onTopicSelect}
            highlightedTopic={highlightedTopic}
            stackIds={stackIds}
            topics={data.categories}
          />
        </div>
        <div
          style={{
            flex: '1 0 100%',
            transition: `opacity 0.25s, grid-template-rows ${
              showCards ? '0.4s 0.25s' : '0.8s 0.4s'
            }, min-height 0.4s ease-out`,
          }}
          ref={childrenRef}
          className={`grid transform ${
            showCards ? 'animate-slide-topic-left' : 'animate-slide-topic-right'
          }`}
        >
          {topic && <SelectedCards topic={topic} onClose={onClose} topicStacks={topicStacks} />}
        </div>
      </div>
    </Card>
  );
};

export default TopicsSection;
